import { Component, OnInit } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { SubSink } from 'subsink';
import { FeatureToggle, TeamUserRole } from '@Enums';
import { RoutingService, TeamsService, UserAdminService, FeatureManagerService, RoutesEnum } from '@Services';
import { SharedModule } from '@Shared/shared.module';
import { TableColumnDefinition } from 'app/shared/components/mrs-table/models/table-models';
import { EditUsersComponent } from '../../edit-users/edit-users.component';
import { EditUsersV2Component } from '../../edit-users-v2/edit-users-v2.component';
import { TeamMembersTableComponent } from './team-members-table.component';
import { TeamMembersScheduleComponent } from './team-members-schedule.component';
import moment from "moment";
import { AppService } from "@App";
import { EditTeamComponent } from "../edit-team/edit-team.component";

@Component({
    selector: 'team-overview',
    host: { 'class': 'content' },
    templateUrl: './team-overview.component.html',
    styleUrls: ['./team-overview.component.scss'],
    imports: [
        SharedModule,
        TeamMembersTableComponent,
        TeamMembersScheduleComponent
    ]
})
export class TeamOverviewComponent implements OnInit {
    isWrite = false;
    isFull = false;
    hasAccessToManageTeams = false;

    selectedDate: Date = new Date();
    teamId: any = null;
    teamName: any = null;
    teamClientId: any = null;
    private subs = new SubSink();
    teamMembers: any;
    userProfileV2: boolean;
    displayedColumns: TableColumnDefinition[];
    accounts: any[];
    
    editUsersDialogRef: MatDialogRef<EditUsersComponent>;
    editUsersV2DialogRef: MatDialogRef<EditUsersV2Component>;

    teamDetails: any;

    editTeamDialogRef: MatDialogRef<EditTeamComponent>;

    constructor(
        private appService: AppService,
        private route: ActivatedRoute,
        private routingService: RoutingService,
        private teamsService: TeamsService,
        private userAdminService: UserAdminService,
        private readonly featureManagerService: FeatureManagerService,
        public dialog: MatDialog) {
        this.route.paramMap.subscribe(params => {
            this.teamId = params.get('teamId');
        });
        this.route.queryParamMap.subscribe(params => {
            this.teamClientId = params.get('teamClientId');
        });
        this.route.queryParamMap.subscribe(params => {
            this.teamName = params.get('teamName');
        });
    }

    ngOnInit(): void {
        this.isWrite = this.appService.checkACL('Manage Teams', 'W');
        this.isFull = this.appService.checkACL('Manage Teams', 'F');
        this.hasAccessToManageTeams = this.isWrite || this.isFull;
        this.getTeamDetails();
        this.userProfileV2 = this.featureManagerService.getByName(FeatureToggle.GlobalUsersUseUserProfileV2).enabled;
        this.setupTableInputs();
        this.setupClients();
    }

    editTeams() {
        this.editTeamDialogRef = this.dialog.open(EditTeamComponent, {
            width: '80%',
            maxWidth: '600px',
            height: 'auto',
            maxHeight: '90vh',
            data: {
                teams: this.teamDetails
            }
        });
        this.subs.add(this.editTeamDialogRef.afterClosed().subscribe(result => {
            if (result) {
                this.getTeamDetails();
            }
        }));
    }

    setSelectedDate(day: Date) {
        this.selectedDate = day;
    }

    getTeamDetails() {
        this.subs.add(this.teamsService.getTeamDetails(this.teamClientId, this.teamId).subscribe(data => {
            if (data) {
                this.teamDetails = data[0] ?? null;
                if (this.teamDetails && this.teamDetails.teamName) {
                    this.teamName = this.teamDetails.teamName;
                }

                if (this.teamDetails)
                {
                    for (const element of this.teamDetails.teamUsers) {
                        element.lastSignInModified = element.lastLoginDate === null ? 'Never' : this.formatLastSignInDate(element.lastLoginDate)
                    }
                    this.teamMembers = this.teamDetails.teamUsers.filter(x => x.teamRoleId != TeamUserRole.AdminOnly);
                }
            }
        }));
    }

    formatLastSignInDate(date: string): string {
        if (!date) {
            return 'Never';
        }

        const mDate = moment(date, ['YYYY-MM-DD', 'M/D/YYYY', moment.ISO_8601], true);
        if (!mDate.isValid()) {
            return 'Never';
        }
        const daysDiff = moment().diff(mDate, 'days');
        if (daysDiff > 1 && daysDiff < 8) {
            return mDate.format('dddd');
        }
        if (daysDiff === 1) {
            return 'Yesterday';
        }
        if (daysDiff === 0) {
            return 'Today';
        }

        const formattedDate = mDate.format('l');

        return formattedDate;
    }

    setupTableInputs() {
        this.displayedColumns = [
            {
                def: 'name',
                header: 'Name & Email',
                sortFields: ['name'],
                columnStyles: {
                    'width': '48%',
                },
                rowDefs: [
                    {
                        className: 'team-member-name',
                        dataName: 'name',
                    },
                    {
                        className: 'team-member-email',
                        dataName: 'email',
                    }
                ]
            },
            {
                def: 'role',
                header: 'Role',
                sortFields: ['userRole'],
                columnStyles: {
                    'width': '12%'
                },
                rowDefs: [
                    {
                        className: '',
                        dataName: 'userRole'
                    }
                ]
            },
            {
                def: 'activeCasesCount',
                header: 'Active Cases',
                sortFields: ['activeCasesCount'],
                columnStyles: {
                    'width': '12%'
                },
                rowDefs: [
                    {
                        className: '',
                        dataName: 'activeCasesCount'
                    }
                ]
            },
            {
                def: 'lastSignInModified',
                header: 'Last Sign in',
                sortFields: ['lastSignInModified'],
                columnStyles: {
                    'width': '12%'
                },
                rowDefs: [
                    {
                        className: '',
                        dataName: 'lastSignInModified'
                    }
                ]
            },
            {
                def: 'actions',
                header: '',
                columnStyles: {
                    'width': '16%'
                },
                isAction: true,
                actions: [
                    {
                        name: 'View',
                        icon: 'square-edit-outline',
                        actionType: 'view',
                        permission: true
                    },
                ]
            }
        ];
    }

    tableAction(event) {
        this.subs.add(this.userAdminService.getUser(event.record?.id).subscribe(result => {
            this.viewUser(result);
        }));
    }

    setupClients() {
        this.appService.getData('Accounts').subscribe((response) => {
            this.accounts = response.data;
        });
    }

    viewUser(user: any): void {
        if (this.userProfileV2) {
            this.editUsersV2DialogRef = this.dialog.open(EditUsersV2Component, {
                minWidth: '1000px',
                width: '50%',
                minHeight: 'min(820px, 90vh)',
                height: '80%',
                disableClose: true,
                data: {
                    user: user,
                    accounts: this.accounts,
                    isReadOnly: false
                }
            });
        } else {
            this.editUsersDialogRef = this.dialog.open(EditUsersComponent, {
                width: '50%',
                data: {
                    user: user,
                    accounts: this.accounts
                }
            });
        }
    }

    goToTeams() {
        this.routingService.navigateToRoute(RoutesEnum.teams);
    }

    reloadTeam() {
        this.getTeamDetails();
    }
}
